// Angular
import { Injectable } from '@angular/core';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import {
  IEventDetail,
  IGetEventsDetailsResponse,
  IPoolTotalDataModel,
  IRace
} from '@amwager/util-ts';

/**
 * Bookkeeping row for multi-race exotic pool handles (DBL, PK4, etc.)
 */
export interface IStoragePoolTotal {
  race: number;
  details: Array<{
    startRace: number;
    endRace: number;
    poolName: string;
    poolTotal: number;
  }>;
}

/**
 * Return type of ResultsService.buildPoolTotals
 */
export interface IBuildPoolTotalsResult {
  fromPoolsTotal: IPoolTotalDataModel[];
  fromTotalHandle: number;
}

/**
 * Pure data helpers for the race-results screen
 */
@Injectable({ providedIn: 'root' })
export class ResultsService {

  /**
   * Roll exotic-pool handles forward across finished races
   * @param {IStoragePoolTotal[]} fromStoragePoolTotal running bookkeeping array
   * @param {Record<string, number>} eventPoolTotal PoolsTotal blob from the tote API
   * @param {number} fromTotalHandle current running handle for the race
   * @param {any[]} fromPoolPrices pool-prices list used to decide which pools are summed
   * @param {IPoolTotalDataModel[]} fromPoolsTotal current `{Handle, Id}` pool totals list
   * @param {number} raceId race being processed
   * @returns {IBuildPoolTotalsResult} updated pool totals and total handle
   */
  buildPoolTotals(
    fromStoragePoolTotal: IStoragePoolTotal[],
    eventPoolTotal: Record<string, number>,
    fromTotalHandle: number,
    fromPoolPrices: any[],
    fromPoolsTotal: IPoolTotalDataModel[],
    raceId: number
  ): IBuildPoolTotalsResult {

    const storagePoolTotal = fromStoragePoolTotal;
    const poolsTotal = _.mapValues(_.keyBy(eventPoolTotal, 'Id'), 'Handle');
    const newArrayPoolTotal = _.map(poolsTotal, (value, key) => ({
      key,
      value,
      display: false
    }));

    _.remove(storagePoolTotal, { race: raceId });

    const gapMap = {
      DBL: 1, TRP: 2, PK3: 2, PK4: 3,
      PK5: 4, PK6: 5, PK7: 6, PK8: 7,
      PK9: 8, P10: 9, P11: 10, P12: 11
    };

    const monitorArray = _.chain(newArrayPoolTotal)
      .filter((vP: any) => !_.isNil(vP.value) && vP.value !== 0)
      .map((vP: any) => {
        const gap = gapMap[vP.key as keyof typeof gapMap] || 0;
        return gap > 0
          ? {
            startRace: raceId,
            endRace: raceId + gap,
            poolName: vP.key,
            poolTotal: vP.value
          }
          : null;
      })
      .compact()
      .value();

    if (monitorArray.length > 0) {
      storagePoolTotal.push({ race: raceId, details: monitorArray });
    }

    const allDetails = _.flatMap(fromStoragePoolTotal, 'details');
    _.forEach(allDetails, d => {
      if (!_.some(fromPoolsTotal, { Id: d.poolName }) && raceId === d.endRace) {
        fromPoolsTotal = [
          ...fromPoolsTotal || [],
          { Handle: d.poolTotal, Id: d.poolName }
        ];
      }

      // DBL is special — always replace so the last carry wins.
      if (d.poolName === 'DBL' && raceId === d.endRace) {
        fromPoolsTotal = [
          ...(fromPoolsTotal || []).filter(p => p.Id !== 'DBL'),
          { Handle: d.poolTotal, Id: 'DBL' }
        ];
      }
    });

    const poolIds = _.chain(fromPoolPrices)
      .filter(item => _.has(item, 'Prices') && item?.Prices?.length > 0)
      .map('PoolId')
      .value();

    fromPoolsTotal = _.filter(fromPoolsTotal, item => _.includes(poolIds, item.Id));
    fromTotalHandle = _.sumBy(fromPoolsTotal, 'Handle');

    return { fromPoolsTotal, fromTotalHandle };
  }

  /**
   * Format a date as `MM-DD-YYYYZ` for the RCN replay endpoint
   * @param {any} date Date input
   * @returns {string} Formatted date string
   */
  toDateUTC(date: any): string {
    return moment(date).format('MM-DD-YYYY[Z]');
  }

  /**
   * Filter events to those Finished or with at least one finished race
   * @param {IGetEventsDetailsResponse[]} events All events list
   * @returns {IGetEventsDetailsResponse[]} Events with results to show
   */
  filterFinishedEvents(
    events: IGetEventsDetailsResponse[] | undefined | null
  ): IGetEventsDetailsResponse[] {
    if (_.isEmpty(events)) return [];

    return _.filter(events || [], (event) => {
      const races = event?.ToteResponse?.EventDetail?.Races || [];
      const hasFinishedRace = races.some(
        (race: { RaceStatus: number }) => race?.RaceStatus === 3
      );
      return (
        event?.ToteResponse?.EventDetail?.EventStatus === 'Finished'
        || hasFinishedRace
      );
    });
  }

  /**
   * Shape raw GetEventsDetails events like the reducer does so the
   * results view can consume historical-date fetches through the same
   * code paths as today's store-backed events (id, per-race
   * runId/eventId, currentRace, eventName).
   * @param {IGetEventsDetailsResponse[]} events Raw events response
   * @returns {IGetEventsDetailsResponse[]} Normalized events list
   */
  normalizeEvents(
    events: IGetEventsDetailsResponse[] | undefined | null
  ): IGetEventsDetailsResponse[] {
    if (_.isEmpty(events)) return [];

    return _.map(events || [], (eventTrackRace) => {
      const runId = eventTrackRace?.ToteResponse?.EventDetail?.RunId;
      const eventId = eventTrackRace?.ToteResponse?.EventDetail?.EventId;
      const races = eventTrackRace?.ToteResponse?.EventDetail?.Races || [];

      return {
        id: eventId,
        ...eventTrackRace,
        ToteResponse: {
          ...eventTrackRace?.ToteResponse,
          EventDetail: {
            ...eventTrackRace?.ToteResponse?.EventDetail,
            Races: _.map(races, (race: IRace) => ({
              ...race,
              runId,
              eventId
            })),
            currentRace: {
              ..._.find(races, 'Current'),
              runId,
              eventId
            }
          } as IEventDetail
        },
        eventName: eventTrackRace?.ToteResponse?.EventDetail?.EventName
      } as unknown as IGetEventsDetailsResponse;
    });
  }

  /**
   * Collapse single-leg pool (WIN/PLC/SHW) rows to one entry each
   * @param {any[]} poolPrices raw pool-prices list from the tote response
   * @param {string[]} groupByPoolId single-leg pool ids to collapse
   * @returns {any[]} display pool prices
   */
  buildDisplayPoolPrices(
    poolPrices: any[] | undefined,
    groupByPoolId: string[] = ['WIN', 'PLC', 'SHW']
  ): any[] {
    if (_.isEmpty(poolPrices)) return [];

    const limited = _.uniqBy(
      (poolPrices || [])
        .filter((item: { PoolId: any }) => groupByPoolId.includes(item.PoolId))
        .map((item: { Prices: string | any[] }) => ({
          ...item,
          Prices: Array.isArray(item.Prices) ? item.Prices.slice(0, 1) : item.Prices
        })),
      'PoolId'
    );

    const others = (poolPrices || []).filter(
      (item: { PoolId: any }) => !groupByPoolId.includes(item.PoolId)
    );

    return [...limited, ...others];
  }
}
